import type {Translator} from '@logistics-frontend/core/i18n'
import type {Optional} from '@logistics-frontend/types'
import {
  applySpec,
  equals,
  flow,
  get,
  ifElse,
  invert,
  isBoolean,
  isNotUndefined,
  isString,
  isUndefined,
  pipe,
  toNumber,
  toUndefined,
} from '@logistics-frontend/utils'

import type {OffersOrder, Order, VerticalTariff} from 'src/shared/types'
import {fromPaymentInfo} from 'src/shared/utils'

import {dropEmptyValues} from './drop-empty-values'
import {fromSddExpressDestinationPoints} from './from-sdd-express-destination-points'
import {fromSddExpressParcel} from './from-sdd-express-parcel'
import {fromSddExpressReturnPoint} from './from-sdd-express-return-point'
import {fromSddExpressSourcePoint} from './from-sdd-express-source-point'

const isArrayOfStrings = (value: unknown): value is string[] => Array.isArray(value) && value.every(isString)

const getTariffType: (order: Order) => Optional<VerticalTariff['name']> = get('offerFlow.vertical.name')

const isCargo: (order: Order) => boolean = flow(getTariffType, equals('cargo'))

const toCargoOptions = (order: Order): Optional<string[]> => {
  const value = get('offerFlow.vertical.requirements.cargo_options.selectedValue', order)
  return isArrayOfStrings(value) ? value : undefined
}

const toCargoType: (order: Order) => Optional<string> = flow(
  get('offerFlow.vertical.requirements.cargo_type.selectedValue'),
  String,
)

const toCargoLoaders: (order: Order) => Optional<number> = flow(
  get('offerFlow.vertical.requirements.cargo_loaders.selectedValue'),
  toNumber,
)

const toProCourier: (order: Order) => Optional<boolean> = flow(
  get('offerFlow.vertical.requirements.pro_courier'),
  ifElse(isUndefined, toUndefined, Boolean),
)

const toClientRequirements: (order: Order) => OffersOrder['client_requirements'] = applySpec<
  OffersOrder['client_requirements']
>({
  taxi_class: get('offerFlow.offer.tariff'),
  cargo_options: ifElse(isCargo, toUndefined, toCargoOptions),
  cargo_type: ifElse(isCargo, toCargoType, toUndefined),
  cargo_loaders: ifElse(isCargo, toCargoLoaders, toUndefined),
  pro_courier: toProCourier,
})

const isDueEnabled: (order: Order) => boolean = flow(get('offerFlow.deliveryType'), equals('toTime'))

const fromDue: (order: Order) => Optional<string> = ifElse(
  isDueEnabled,
  get('offerFlow.deliveryInterval.from'),
  toUndefined,
)
const fromIsNotifyClient: (order: Order) => Optional<boolean> = flow(
  get('isNotifyClient'),
  ifElse(isBoolean, invert, toUndefined),
)

export function fromOffersOrder(t: Translator, order: Order): OffersOrder {
  const orderSourcePoint = get('sourcePoint', order)
  const orderDestinationPoints = get('destinationPoints', order)
  const isNightVertical = pipe(order, get('offerFlow.vertical.name'), equals('night'))
  const deliveryInterval = get('offerFlow.deliveryInterval', order)
  const paymentInfo = get('paymentInfo', order)
  const sourcePoint = pipe(orderSourcePoint, fromSddExpressSourcePoint(t))
  const returnPoint = pipe(order, fromSddExpressReturnPoint(t))

  const destinationPoints = isNightVertical ? orderDestinationPoints.slice(0, 1) : orderDestinationPoints
  const mappedDestinationPoints = pipe(destinationPoints, fromSddExpressDestinationPoints(t))

  const preparedDestinationPoints = destinationPoints.map((point, index) => ({
    ...point,
    _id: index + 1,
  }))

  const routePoints = [sourcePoint, ...mappedDestinationPoints, returnPoint]
    .filter(isNotUndefined)
    .map((point, index) => {
      point.visit_order = index + 1
      point.point_id = index

      return point
    })

  const items = fromSddExpressParcel(0, preparedDestinationPoints)

  const body: OffersOrder = {
    items,
    route_points: routePoints,
    due: fromDue(order),
    optional_return: get('isOptionalReturn', order),
    emergency_contact: dropEmptyValues(sourcePoint.contact),
    skip_client_notify: fromIsNotifyClient(order),
    skip_door_to_door: pipe(order, get('offerFlow.vertical.requirements.door_to_door.selectedValue'), invert),
    client_requirements: toClientRequirements(order),
    comment: get('comment', order),
    referral_source: get('_referralSource', order),
  }

  if (paymentInfo) {
    Object.assign(body, {
      pricing_payment_methods: {
        payment_info: fromPaymentInfo(paymentInfo),
      },
    })
  }

  if (isNightVertical) {
    Object.assign(body, {
      same_day_data: {
        delivery_interval: deliveryInterval,
      },
      client_requirements: undefined,
    })
  }

  return body
}
